import { dumpYaml, V1Secret } from "@kubernetes/client-node"
import { ChildProcess, spawn, spawnSync } from 'child_process'
import { lookpath } from 'lookpath'
import * as os from 'os'
import * as path from 'path'
import { fsExist, fsRead, fsRemove, fsWrite, logStream, wait } from "yam-operator/util"
import { K8SResourceType, KVString, OperateFunction, PlanContext } from "yam-types"
import { Settings } from "../settings.d"
import { HelmReleaseConfig } from "../types.d"

const DEFAULT_CHART_REPOS: KVString = { "bitnami": "https://charts.bitnami.com/bitnami" }

const helmOperator: OperateFunction<HelmReleaseConfig> = async (plan, diff, settings: Settings) => {
  if (!diff.hasDiff) {
    return
  }
  for (const releaseConf of diff.newItems) {
    const chart = releaseConf.chartName || releaseConf.name
    plan.log.info(`helm chart '${chart}', release name: ${releaseConf.name} will be installed`)
    await operateHelmChart(plan, releaseConf, false, settings)
  }
  for (const modifiedRelease of diff.modifiedItems) {
    const chart = modifiedRelease.current.chartName || modifiedRelease.current.name
    plan.log.info(`upgrading helm chart '${chart}', release name: ${modifiedRelease.current.name}`)
    await operateHelmChart(plan, modifiedRelease.current, false, settings)
    plan.log.info(`helm chart ${modifiedRelease.current.name} upgraded`)
  }
  diff.deletedItems.forEach(r => {
    plan.log.warn(`removing helm release is not allowed, please run 'helm uninstall ${r.name}' command manually`)
  })
}

const helmUninstaller: OperateFunction<HelmReleaseConfig> = async (plan, diff, settings) => {
  for (const releaseConf of diff.currentItems) {
    const chart = releaseConf.chartName || releaseConf.name
    plan.log.info(`helm chart '${chart}', release name: ${releaseConf.name} will be installed`)
    await operateHelmChart(plan, releaseConf, true, settings)
  }
}

export const operateHelmChart = async (plan: PlanContext, release: HelmReleaseConfig, isDelete: boolean, settings: Settings): Promise<void> => {
  const ns = release.namespace || plan.data.namespace
  if (plan.data.kubeConfigPath && plan.data.kubeConfigPath.indexOf("kind: Config") !== -1) {
    plan.log.error(`passing raw kubeConfig content is not supported in Helm plugin, operation skipped.`)
    return
  }
  const helmValConfPath = path.join(os.tmpdir(), ['values', plan.data.workloadName, release.name, Date.now()].join('-') + '.yaml')

  if (isDelete && plan.data.templateOutput) {
    plan.log.info(`skip 'helm template' for Chart(${release.name}) that would be uninstalled`)
    return
  }
  if (!release.name) {
    plan.log.warn(`skip install helm chart because of empty release name.`)
    return
  }
  // check and download Helm v3 binary if not exists
  const helmExecutable = await checkHelmExecutableAndUpdateRepo(plan, isDelete, settings)
  const operation = plan.data.templateOutput ? 'template' : isDelete ? 'uninstall' : 'upgrade'
  const cmd = [operation, release.name]
  plan.action(async (ctx) => {
    try {
      if (release.values) {
        await fsWrite(helmValConfPath, dumpYaml(release.values))
      }
      const helmSecrets: V1Secret[] = await ctx.k8sClient.find({
        namespace: ns,
        kind: K8SResourceType.Secret
      }, { "owner": "helm", name: release.name })
      const existing = helmSecrets.find(r => r.type === "helm.sh/release.v1" && r.metadata)
      if (existing) {
        if (isDelete) {
          plan.log.info(`helm release '${release.name}' will be uninstalled.`)
        } else {
          plan.log.info(`helm release '${release.name}' already exists in namespace ${ns}, will convert to 'upgrade' operation.`)
          plan.log.info(`helm release last modified at: ${new Date(parseInt(existing.metadata?.labels?.modifiedAt as string) * 1000).toISOString()}`)
        }
      } else {
        if (isDelete) {
          plan.log.warn(`helm release '${release.name}' is not found, can not uninstall.`)
          return
        } else if (!plan.data.templateOutput) {
          cmd.push('--install')
        }
      }
      // compose command
      cmd.push('--kube-context')
      cmd.push(plan.data.kubeContextName)
      if (plan.data.kubeConfigPath) {
        cmd.push('--kubeconfig')
        cmd.push(plan.data.kubeConfigPath)
      }
      cmd.push('--namespace')
      cmd.push(ns)
      if (!isDelete) {
        const repo = release.repo
        if (repo) {
          cmd.push('--repo')
          cmd.push(repo as string)
        }
        if (release.values) {
          cmd.push('-f')
          cmd.push(helmValConfPath)
        }
        // templateOutput not empty means 'yam template' executed
        if (release.wait && !plan.data.templateOutput) {
          cmd.push('--wait')
        }
        if (settings.username) {
          cmd.push('--username')
          cmd.push(settings.username as string)
        }
        if (settings.password) {
          cmd.push('--password')
          cmd.push(settings.password as string)
        }
        if (release.version) {
          cmd.push('--version')
          cmd.push(release.version)
        }
      }
      if (plan.data.dryRun) {
        cmd.push('--dry-run')
      }
      if (!isDelete) {
        cmd.push(release.chartName || release.name)
      }

      // execute command
      ctx.log.info(`---------------- Helm Operator Start ---------------`)
      ctx.log.info(`exec: helm ${settings.password ? cmd.join(' ').replace(settings.password as string, '***') : cmd.join(' ')}`)
      if (!plan.data.dryRun || plan.data.templateOutput) {
        const proc = spawn(helmExecutable as string, cmd, {
          // redirect output
          stdio: ['inherit', 'pipe', 'pipe']
        })
        let output = ''
        proc.stdout.on('data', buffer => {
          // append to templateOutput file if running in template mode
          if (plan.data.templateOutput) {
            output += buffer.toString()
          }
          logStream.write(buffer)
        })
        proc.stderr.on('data', buffer => {
          logStream.write(buffer)
        })
        await onExit(proc)
        if (plan.data.templateOutput) {
          console.log('---\n' + output)
        }
        // you might apply too many yaml(s), get some rest for your Kubernetes to avoid 429
        await wait(1000)
      } else {
        ctx.log.info(`exec: dry-run mode. command won't be executed`)
      }
    } finally {
      if (release.values) {
        await fsRemove(helmValConfPath)
      }
      ctx.log.debug(`tmp file ${helmValConfPath} deleted`)
      ctx.log.info(`---------------- Helm Operator End -----------------`)
    }
  }, `helm-${operation}:${release.name}`)
}

function onExit(childProcess: ChildProcess): Promise<void> {
  return new Promise((resolve, reject) => {
    childProcess.once('exit', (code: number) => {
      if (code === 0) {
        resolve(undefined)
      } else {
        reject(new Error('Spawn process exited with error code: ' + code))
      }
    })
    childProcess.once('error', (err: Error) => {
      reject(err)
    })
  })
}

let helmExecutablePath = ''

async function checkHelmExecutableAndUpdateRepo(plan: PlanContext, disableRepoUpdate: boolean, settings: Settings): Promise<string> {
  if (helmExecutablePath) {
    return helmExecutablePath
  }
  plan.log.info('checking helm binary executable')
  const basePath = path.join(os.homedir(), '.yam')
  // helm version check, use cached path if available
  const helmCachePath = path.join(basePath, 'helm-info-cache.json')
  let cachedResult: HelmCacheResult = {
    lastBinaryCheck: 0, lastBinaryPath: '', lastPublicRepoCheck: 0
  }
  try {
    const now = Date.now()
    let skipVersionCheck = false
    if (await fsExist(helmCachePath)) {
      const cached = await fsRead(helmCachePath)
      cachedResult = JSON.parse(cached.toString()) as HelmCacheResult
      if (now - cachedResult.lastBinaryCheck < binaryCheckPeriod) {
        helmExecutablePath = cachedResult.lastBinaryPath
        skipVersionCheck = true
      }
    }

    if (!skipVersionCheck) {
      const helmExecutable = await lookpath('helm', {
        include: [basePath]
      })
      if (!helmExecutable) {
        throw new Error(`helm binary executable is not found, please download and configure your PATH, refer: https://github.com/helm/helm/releases`)
      }

      const verRes = spawnSync(helmExecutable, ["version"])
      const ver = verRes.stdout.toString()
      if (verRes.stderr.length > 0) {
        plan.log.warn(verRes.stderr.toString())
      }
      if (!ver || ver.indexOf('SemVer:"v2') !== -1) {
        throw new Error(`Helm v2 is not supported, please upgrade to v3 manually, binary path: ${helmExecutable}, ${ver}`)
      }
      plan.log.info(`helm executable: ${ver.substring(ver.indexOf('{') + 1, ver.length - 1)}.`)
      helmExecutablePath = helmExecutable
      cachedResult.lastBinaryPath = helmExecutable
      cachedResult.lastBinaryCheck = now
    }

    if (!settings.disablePublicRepo) {
      if (now - cachedResult.lastPublicRepoCheck < publicRepoCheckPeriod) {
        disableRepoUpdate = true
      } else {
        for (const name in DEFAULT_CHART_REPOS) {
          const result = spawnSync(helmExecutablePath, ['repo', 'add', name, DEFAULT_CHART_REPOS[name].toString()])
          plan.log.debug(`${result.stdout.toString()}${result.stderr.toString()}public helm repo added.`)
        }
        // cache public repo update result
        cachedResult.lastPublicRepoCheck = now
      }
    }
    if (settings.repos) {
      const repos = settings.repos
      for (const name in repos) {
        const result = spawnSync(helmExecutablePath, ['repo', 'add', name, repos[name].toString()])
        plan.log.debug(`${result.stdout.toString()}${result.stderr.toString()}customized helm repos added.`)
        disableRepoUpdate = false
      }
    }
    if (!disableRepoUpdate) {
      const result = spawnSync(helmExecutablePath, ['repo', 'update'])
      plan.log.info(`${result.stdout.toString()}${result.stderr.toString()}helm repo update finished.`)
    }
  } catch (e) {
    plan.log.error(`can not execute helm command, binary path: ${helmExecutablePath}. ${e.message}`)
    throw e
  } finally {
    await fsWrite(helmCachePath, JSON.stringify(cachedResult))
  }
  return helmExecutablePath
}

const binaryCheckPeriod = 7 * 86400 * 1000
const publicRepoCheckPeriod = 3 * 86400 * 1000

type HelmCacheResult = {
  lastBinaryPath: string
  lastBinaryCheck: number
  lastPublicRepoCheck: number
}

export { helmOperator, helmUninstaller }

